import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { ApiService } from './api.service';
import { switchMap, tap, map } from 'rxjs/operators';
import { RegionTypeEnum } from '@app/shared/enums';
import { StringUtility } from '@app/shared/string-utility';
import { NullableString } from '@app/shared/models/nullable';

@Injectable({
  providedIn: 'root',
})
export class RegionMunicipalitiesService extends ApiService {
  constructor(public http: HttpClient) {
    super(http);
  }

  baseUri = 'socrates/authz/api';
  authzViewsUri = 'socrates/authz/api/views';

  private municipalityRegionMappingStore: BehaviorSubject<any[]> = new BehaviorSubject<any>([]);
  public municipalityRegionMapping$ = this.municipalityRegionMappingStore.asObservable();

  private selectedKeys: string = ['Guid', 'Name', 'MunicipalityKey', 'PrimaryAdminGuid'].join(',');

  public postMunicipalityRegionMapping(data: any): Observable<any> {
    return this.http.post<any>(this.endpoint(this.baseUri, `regions/${data.regionId}/municipalities/${data.municipalityId}`), data);
  }

  public deleteRegionMunicipalityMapping(regionId: any, municipalityId: any): Observable<any> {
    return this.http.delete<any>(this.endpoint(this.baseUri, `regions/${regionId}/municipalities/${municipalityId}`));
  }

  private mapRegionType(regionMunicipalities: RegionMunicipalities[], regionType: string): RegionMunicipalities | undefined {
    return regionMunicipalities.find((regionMunicipality: RegionMunicipalities) =>
      StringUtility.areStringsIdentical(regionMunicipality.region?.regionType?.code, regionType),
    );
  }

  private mapRegionIds(regionMunicipalities: RegionMunicipalities[], regionType: string): string[] {
    const regionsArray = regionMunicipalities.filter((regionMunicipality: RegionMunicipalities) =>
      StringUtility.areStringsIdentical(regionMunicipality.region?.regionType?.code, regionType),
    );

    const regionIds: string[] = regionsArray.map((regionMunicipality) => {
      if (regionMunicipality.regionId) {
        return regionMunicipality.regionId;
      }
      return '';
    });
    return regionIds;
  }

  private mapRegionNames(regionMunicipalities: RegionMunicipalities[], regionType: string): string[] {
    const regionsArray = regionMunicipalities.filter((regionMunicipality: RegionMunicipalities) =>
      StringUtility.areStringsIdentical(regionMunicipality.region?.regionType?.code, regionType),
    );

    const regionNames: string[] = regionsArray.map((regionMunicipality) => {
      if (regionMunicipality.region && regionMunicipality.region.name) {
        return regionMunicipality.region.name;
      }
      return '';
    });
    return regionNames;
  }

  private mapMunicipalityRegionDetails(regionMunicipalityResponse: MunicipalitiesResponse[]): MunicipalityRegionHierarchy[] {
    return regionMunicipalityResponse.map((response: MunicipalitiesResponse) => ({
      municipalityId: response?.guid ?? '',
      municipalityKey: response?.municipalityKey ?? '',
      municipalityName: response?.name ?? '',
      adminRegionId: this.mapRegionIds(response.regionMunicipalities, RegionTypeEnum.adminAreaRegion),
      adminRegionMappingId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.adminAreaRegion)?.guid,
      adminRegionName: this.mapRegionNames(response.regionMunicipalities, RegionTypeEnum.adminAreaRegion),
      backOfficeRegionId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.backOfficeRegion)?.regionId,
      backOfficeRegionMappingId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.backOfficeRegion)?.guid,
      backOfficeRegionName: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.backOfficeRegion)?.region?.name,
      customerServiceRegionId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.customerServiceRegion)?.regionId,
      customerServiceRegionMappingId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.customerServiceRegion)?.guid,
      customerServiceRegionName: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.customerServiceRegion)?.region?.name,
      kamRegionId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.kamRegion)?.regionId,
      kamRegionMappingId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.kamRegion)?.guid,
      kamRegionName: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.kamRegion)?.region?.name,
      poHoldingRegionId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.poHoldingRegion)?.regionId,
      poHoldingRegionMappingId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.poHoldingRegion)?.guid,
      poHoldingRegionName: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.poHoldingRegion)?.region?.name,
      primaryAdminFirstName: response?.primaryAdminGu?.firstname,
      primaryAdminGuid: response?.primaryAdminGuid,
      primaryAdminLastName: response?.primaryAdminGu?.lastname,
      serviceRegionId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.serviceRegion)?.regionId,
      serviceRegionMappingId: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.serviceRegion)?.guid,
      serviceRegionName: this.mapRegionType(response.regionMunicipalities, RegionTypeEnum.serviceRegion)?.region?.name,
    }));
  }

  public getMunicipalityRegionMapping(): Observable<MunicipalityRegionHierarchy[]> {
    return this.http
      .get<
        MunicipalitiesResponse[]
      >(this.endpoint(this.baseUri, `municipalities?$select=${this.selectedKeys}&$expand=PrimaryAdminGu,RegionMunicipalities($select=RegionId,Guid;$expand=Region($select=Name;$expand=RegionType($select=Code)))`))
      .pipe(
        map((regionMunicipalityResponse: MunicipalitiesResponse[]): MunicipalityRegionHierarchy[] =>
          this.mapMunicipalityRegionDetails(regionMunicipalityResponse),
        ),
        tap((municipalityRegion: MunicipalityRegionHierarchy[]) => {
          this.municipalityRegionMappingStore.next(municipalityRegion);
        }),
      );
  }

  public putMunicipalityRegionMapping(oldRegionId: string[], data: any): Observable<any> {
    return this.deleteRegionMunicipalityMapping(oldRegionId, data.municipalityId).pipe(switchMap(() => this.postMunicipalityRegionMapping(data)));
  }

  public getMunicipalityRegionMappingByMunicipalityId(municipalityId: string): Observable<MunicipalityRegionHierarchy[]> {
    return this.http
      .get<
        MunicipalitiesResponse[]
      >(this.endpoint(this.baseUri, `municipalities?$select=${this.selectedKeys}&$expand=PrimaryAdminGu($select=firstname,lastname),RegionMunicipalities($select=RegionId,Guid;$expand=Region($select=Name;$expand=RegionType($select=Code)))&$filter=guid eq ${municipalityId}`))
      .pipe(
        map((regionMunicipalityResponse: MunicipalitiesResponse[]): MunicipalityRegionHierarchy[] =>
          this.mapMunicipalityRegionDetails(regionMunicipalityResponse),
        ),
      );
  }
}

export interface RegionMunicipality {
  guid: string;
  regionId: string;
  municipalityId: string;
}

export interface MunicipalityRegionHierarchy {
  municipalityId: string;
  municipalityName: string;
  municipalityKey: string;
  adminRegionId?: string[];
  adminRegionMappingId?: NullableString;
  adminRegionName?: string[];
  backOfficeRegionId?: NullableString;
  backOfficeRegionMappingId?: NullableString;
  backOfficeRegionName?: NullableString;
  customerServiceRegionId?: NullableString;
  customerServiceRegionMappingId?: NullableString;
  customerServiceRegionName?: NullableString;
  kamRegionId?: NullableString;
  kamRegionMappingId?: NullableString;
  kamRegionName?: NullableString;
  poHoldingRegionId?: NullableString;
  poHoldingRegionMappingId?: NullableString;
  poHoldingRegionName?: NullableString;
  primaryAdminFirstName?: NullableString;
  primaryAdminGuid?: NullableString;
  primaryAdminLastName?: NullableString;
  serviceRegionId?: NullableString;
  serviceRegionMappingId?: NullableString;
  serviceRegionName?: NullableString;
}

export interface MunicipalitiesResponse {
  guid: string;
  municipalityKey: string;
  name: string;
  primaryAdminGu: { firstname: string; lastname: string } | null;
  primaryAdminGuid: NullableString;
  regionMunicipalities: RegionMunicipalities[];
}

export interface RegionMunicipalities {
  guid: string;
  region: Region;
  regionId: string;
}

export interface Region {
  name: string;
  regionType: { code: string };
}
